import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, Paper, Collapse, IconButton, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Alert } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useUser } from "../../context/userContext";
import { getSalesById, updateSalesByAdmin, generateSaleForms } from "../../api/salesApi";
import { createLog } from "../../api/logApi";
import { toNullIfEmptyOrZero } from "../../utils/priceUtils";
import handleAgentName from "../../utils/userUtils";
import GenericSalesForm from "../common/genericSalesForm";
import { getPricing, getPricingHistories } from "../../api/resourceApi";
import { getAllAgents, getAllAgentGroups, getAgentsByGroupId, getAgentDetailsByAgentId } from "../../api/userApi";
import { convertDateStringToDateTime } from "../../utils/datetimeUtils";
import LogViewer from "../common/logViewer";
import SalesDocumentsPanel from "../sales/salesDocumentsPanel";
import ProjectTimeline from "../sales/projectTimeline";
import SaleFormDetails from "../sales/saleFormDetails";
import { buildFrozenPricing } from "../../utils/frozenPricing";
import { methodBreakdown, termForMethod, factorForMethod, ccFromCash, isMixMethod, mixBreakdown } from "../../utils/ccPricing";
import { buildClickupExportMsg } from "../sales/clickupExport";
import { requestPaymentLink } from "../../api/paymentLinkApi";
import { getRefundBySale, createRefundRequest, cancelRefundRequest } from "../../api/refundApi";
import { PAYMENT_METHOD_OPTIONS } from '../../utils/ccPricing';

const PP = "'Poppins', sans-serif";

// Payment-link request: methods + the banks that offer a PAYMENT LINK for each instalment
// term. Source of truth = the merchant "Payment link by credit card" chart (confirmed by
// user 2026-07-28). Public Bank + CIMB DO issue payment links (previously wrongly excluded).
// "Any Bank" always appended as a catch-all.
const PL_METHODS = ['One-off payment', 'Instalment 6 months', 'Instalment 12 months', 'Instalment 18 months', 'Instalment 24 months', 'Instalment 36 months', 'Instalment 48 months', 'Instalment 60 months'];
// All 12 banks that issue payment links (used for one-off + the 24/36-month terms).
const PL_ALL = ['Maybank', 'Standard Chartered', 'Public Bank', 'AmBank Group', 'Hong Leong Bank', 'CIMB', 'HSBC', 'RHB', 'Affin Bank', 'UOB', 'OCBC', 'Alliance Bank'];
// 6/12-month: everyone except Alliance Bank (Alliance starts at 24M on the chart).
const PL_6_12 = PL_ALL.filter(b => b !== 'Alliance Bank');
const PL_INSTALMENT_BANKS = {
    'Instalment 6 months':  PL_6_12,
    'Instalment 12 months': PL_6_12,
    'Instalment 18 months': ['Maybank', 'Standard Chartered', 'HSBC'],
    'Instalment 24 months': PL_ALL,
    'Instalment 36 months': PL_ALL,
    'Instalment 48 months': ['Maybank', 'Standard Chartered', 'Hong Leong Bank'],
    'Instalment 60 months': ['Maybank', 'Standard Chartered', 'Hong Leong Bank'],
};
const PL_ALL_BANKS = PL_ALL;   // one-off payment-link banks — all 12
const banksForMethod = (m) => [...(m === 'One-off payment' ? PL_ALL_BANKS : (PL_INSTALMENT_BANKS[m] || [])), 'Any Bank'];

// Colours for the sale-status chip beside Save Changes.
const STATUS_STYLES = {
    Active:   { fg: '#15803D', bg: '#F0FDF4', bd: '#BBF7D0' },
    Pending:  { fg: '#B45309', bg: '#FEF3C7', bd: '#FDE68A' },
    Refunded: { fg: '#B91C1C', bg: '#FEE2E2', bd: '#FECACA' },
    Archived: { fg: '#475569', bg: '#F1F5F9', bd: '#E2E8F0' },
    'Dispute Pricing': { fg: '#C2410C', bg: '#FFF7ED', bd: '#FED7AA' },
};

export default function EditSalePage() {
    const { salesId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();

    const isSuperAdmin = user?.roles?.includes("Super Admin");
    // Co-broke "Adjustment Edit": only a Super Admin carrying the AdjustmentEdit
    // right may add/change/remove the co-broke agent on an existing sale. The
    // backend re-verifies this right AND blocks the change if commission is
    // already paid (void the slip first). Everyone else sees co-broke read-only.
    const canEditCobroke = isSuperAdmin && !!user?.roleRights?.includes("AdjustmentEdit");
    const isAsternAdmin = user?.roles?.includes("Astern Admin");
    const hasGroupAdmin = user?.roles?.includes("Group Admin");
    // A Group Admin may ALSO carry the Admin role (role overlap — e.g. joyce). Treat them
    // as a GROUP ADMIN (edits scoped to their OWN group, see saleInMyGroup below), NOT a
    // pure Admin. Only Super Admin / Accountant outrank Group Admin here. (user 2026-06-29)
    const isGroupAdmin = hasGroupAdmin && !user?.roles?.some(r => ['Super Admin', 'Accountant'].includes(r));
    // Pure Admin / Astern Admin (with NO Group Admin role) = admin-only → read-only.
    const isAdminOnly = ((user?.roles?.includes("Admin") && !user?.roles?.includes("Super Admin") && !user?.roles?.includes("Accountant")) || isAsternAdmin) && !hasGroupAdmin;
    // Group Admin now edits a PENDING sale fully, like a sales person (user 2026-06-11) —
    // no longer remark-only. (Previously: const isRemarkOnly = isGroupAdmin;) So a Group
    // Admin gets agentEditable on Pending sales (read-only once Active), same as an agent.
    const isRemarkOnly = false;
    // Doc upload is allowed for everyone who reaches the page.
    const canUploadDocs = !isRemarkOnly || isGroupAdmin;
    // Accountant / Super Admin may always edit. (Status-aware edit gates that also
    // let the agent edit a PENDING sale are computed below, after saleStatus.)
    const isPrivileged = !isAsternAdmin && !isRemarkOnly && user?.roles?.some(r => ['Accountant', 'Super Admin'].includes(r));
    // Pricing LENS (display + save basis) — Group Admin uses the SAME stored-price
    // lens as Accountant/Super Admin so the detail view shows the CURRENT stored
    // OriginalPrice (matching the list), not the stale frozen form snapshot. This is
    // display/basis only; the EDIT permission stays status-aware (Group Admin can
    // only change a PENDING sale, via agentEditable). Fixes the "list updated but
    // inside still old price" mismatch a Group Admin saw. (user 2026-06-26)
    const pricingLens = isPrivileged || isGroupAdmin;
    // Only Super Admin / Accountant may manually override the sale status.
    const canEditStatus = user?.roles?.some(r => ['Accountant', 'Super Admin'].includes(r));
    // Project Timeline milestone dates — Accountant / Super Admin / Admin only.
    // A GROUP ADMIN can SEE the dates but not move them: a date is an agreement with the
    // engineer and the customer, and it is made in one place. Everyone else (agents,
    // leaders, viewer, …) is read-only too.
    // (user 2026-10-05: "only admin can adjust the time ... group admin cannot adjust timeline")
    const canEditTimeline = user?.roles?.some(r => ['Accountant', 'Super Admin', 'Admin'].includes(r));
    // Referral on a sale is locked once an agent saves — only Accountant / Super
    // Admin can change it during amend. Agents (and other roles) view-only here.
    const canEditReferral = user?.roles?.some(r => ['Accountant', 'Super Admin'].includes(r));

    // Clean list (matches Create Sale). Legacy values (CC / CC 36M / CC 60M / LOAN)
    // were removed; a sale still holding one is re-added below so it still displays.
    // Values MUST match Create Sale ("creditCard12/24/36/60", "CASH") so a sale
    // saved from the create form matches an option here — otherwise the saved
    // value gets appended as a stray raw option (e.g. "creditCard60").
    // ONE list, shared with the create form and the RSC. It used to be a copy here,
    // and copies drift: the admin modal's copy was missing 12-month and MIX and wrote
    // UPPERCASE values, which is why production holds both CREDITCARD36 (90 sales) and
    // creditCard60 (25) for what is the same thing. (user 2026-10-11: "form and rsc need
    // to be very similarly linked")
    const BASE_PAYMENT_METHODS = PAYMENT_METHOD_OPTIONS;
    // Snap any saved payment-method string (legacy "CREDITCARD60", "CC 60M", etc.)
    // onto the canonical create-form value so it matches a dropdown option.
    const normalizePaymentMethod = (pm) => {
        const s = String(pm || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!s) return '';
        if (s === 'cash') return 'CASH';
        if (s.includes('mix')) return 'mix';
        if (s.includes('12')) return 'creditCard12';
        if (s.includes('24')) return 'creditCard24';
        if (s.includes('36')) return 'creditCard36';
        if (s.includes('60')) return 'creditCard60';
        return pm; // unknown (generic CC / LOAN) → keep raw; appended for display
    };

    const [sale, setSale] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [logRefreshKey, setLogRefreshKey] = useState(0);
    const [historyOpen, setHistoryOpen] = useState(false);

    // Request Payment Link dialog
    const [plOpen, setPlOpen] = useState(false);
    const [plBank, setPlBank] = useState("");
    const [plMethod, setPlMethod] = useState("One-off");
    const [plAmount, setPlAmount] = useState("");
    const [plNumLinks, setPlNumLinks] = useState(1);
    const [plSubmitting, setPlSubmitting] = useState(false);
    const [plMsg, setPlMsg] = useState(null); // { type, text }
    const [refundReq, setRefundReq] = useState(null);   // open (Pending) refund request, or null
    const [refundDlgOpen, setRefundDlgOpen] = useState(false);
    const [refundReason, setRefundReason] = useState("");
    const [refundBusy, setRefundBusy] = useState(false);
    const [formDetailsOpen, setFormDetailsOpen] = useState(false);
    const [formPayload, setFormPayload] = useState(null);
    // RSC-style promos (manual sales only) — restored from the snapshot on load.
    const [promoBackupbox, setPromoBackupbox] = useState(false);
    const [promoFirst640, setPromoFirst640] = useState(false);
    const [promoPerBatt, setPromoPerBatt] = useState(false);
    const [clickupCopied, setClickupCopied] = useState(false);
    const [saleStatus, setSaleStatus] = useState('Pending');
    const statusTouched = useRef(false);
    // A Group Admin may edit ONLY sales in their OWN group — fetch their group id once.
    const [myGroupId, setMyGroupId] = useState(null);
    useEffect(() => {
        if (!isGroupAdmin || !user?.userId) return;
        let cancelled = false;
        getAgentDetailsByAgentId(user.userId)
            .then(r => { if (!cancelled) setMyGroupId(r?.data?.groupId ?? null); })
            .catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGroupAdmin, user?.userId]);

    // Edit permissions are STATUS-aware:
    //  - Accountant / Super Admin: may always edit.
    //  - Sales agent (not admin-only / remark-only): may edit ALL inputs only while
    //    the sale is PENDING. Once Active / Refunded / Archived it is read-only for them.
    const isPendingSale = (saleStatus || 'Pending') === 'Pending';
    // Group Admin is SCOPED to their own group: editable only when the sale's agent is in
    // the Group Admin's group. Non-group-admins have no group scope (true). Until myGroupId
    // loads, a Group Admin is gated closed so they can't edit the wrong group.
    const saleInMyGroup = !isGroupAdmin
        ? true
        : (myGroupId != null && Number(sale?.agentDetails?.groupId) === Number(myGroupId));
    const agentEditable = !isAdminOnly && !isRemarkOnly && isPendingSale && saleInMyGroup;
    const canEditDetails = isPrivileged || agentEditable;   // customer / product / form details
    const canEditPricing = isPrivileged || agentEditable;   // pricing + add-ons
    const canEditOriPrice = canEditPricing;
    const [lineItems, setLineItems] = useState([]);

    const [originalPrice, setOriginalPrice] = useState(0);
    // Original Cash Price — the single cash input (editor override). Blank = follow cashRef.
    const [ocp, setOcp] = useState('');
    // ATAP dated-list cash reference, pushed up by GenericSalesForm. The cash anchor when
    // ocp is blank — drives the derived CC basis + commission. NEVER back-derived.
    const [cashRef, setCashRef] = useState(0);
    const [finalPrice, setFinalPrice] = useState(0);
    const [addOnPrice, setAddOnPrice] = useState(0);
    const [asternDiscount, setAsternDiscount] = useState(0);
    const [agentDiscount, setAgentDiscount] = useState(0);
    // Upline / Team discount (2026-08-19). Two NEW discount buckets funded by the seller's
    // upline(s) (comes off DOC) and the leader(s) (comes off TOC). They ride the EXACT same
    // gate as Astern/Agent — canEditPricing (agent while Pending, Accountant / Super Admin
    // any time). No separate lock, no timer. Negative DOC/TOC is a legitimate outcome and is
    // never clamped here. See docs/superpowers/specs/2026-08-18-upline-team-discount-design.md
    const [uplineDiscount, setUplineDiscount] = useState(0);
    const [teamDiscount, setTeamDiscount] = useState(0);
    // "Deduct SST" (commercial): the typed discount deducts the SST first. Restored from
    // FormPayloadJson.deductSst below, persisted on save. (user 2026-08-10)
    const [deductSst, setDeductSst] = useState(false);
    // Item 5 — direct CC-pricing override (Super Admin / Accountant only). When a CC term
    // is selected the four CC figures auto-populate (derived, matching the display card).
    // Once the editor types in any of them this holds {original,astern,agent,final} and the
    // sale is stored straight from it (commission base = original − astern). Cleared whenever
    // the payment method changes → the figures re-derive from the cash anchor (auto-populate).
    const [ccOverride, setCcOverride] = useState(null);
    // MIX (cash + CC): cash paid upfront + the term the remainder finances on. Stored in
    // FormPayloadJson (no schema change). (user 2026-07-22)
    const [mixCashAmount, setMixCashAmount] = useState('');
    const [mixTerm, setMixTerm] = useState(36);
    // The card's bank. 60-month rates are the bank's own (7.20%-14.00%), so reopening a
    // sale has to re-derive on the SAME bank it was created with or the price silently
    // moves. Read from / written back to formPayloadJson - no schema change.
    // (user 2026-10-29: RSC is the model; this page follows it)
    const [ccBank, setCcBank] = useState('');
    const [ccBankMethod, setCcBankMethod] = useState('');
    // LIVE Total Quotation (Final + Add-on in the paying basis) reported by the form, so the
    // payment-documents / Cust-Progress denominator follows the current pricing. (user 2026-07-22)
    const [liveQuotationTotal, setLiveQuotationTotal] = useState(0);
    // The sale's stored (Section-2) values, kept so an UNTOUCHED edit re-saves them
    // unchanged. The cash inputs (Section 1) start blank — only when the agent edits
    // them does the price recompute. No silent re-pricing of existing sales.
    const [storedPricing, setStoredPricing] = useState(null);

    const [customerName, setCustomerName] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [customerAddress, setCustomerAddress] = useState("");
    const [customerState, setCustomerState] = useState("");
    const [customerCity, setCustomerCity] = useState("");
    const [customerPostcode, setCustomerPostcode] = useState("");

    // Co-broke editing (Adjustment Edit). Options + selection mirror create-sales.
    // Only populated/editable when canEditCobroke; otherwise the form shows the
    // existing co-broke read-only (no-op setters, as before).
    const [coBrokeGroupOptions, setCoBrokeGroupOptions] = useState([]);
    const [coBrokeNameOptions, setCoBrokeNameOptions] = useState([]);
    const [selectedCoBrokeGroupId, setSelectedCoBrokeGroupId] = useState("");
    const [selectedCoBrokeAgentId, setSelectedCoBrokeAgentId] = useState("");

    const [productList, setProductList] = useState([]);
    // Wattage of the active ATAP list (kWp ÷ panels) so the wattage field follows the
    // sale's pricing list (its snapshot, or live) — same rule as create.
    const atapPanelWatt = useMemo(() => {
        const row = (productList || []).find(r => Number(r.panelQty) > 0 && Number(r.energy) > 0);
        return row ? Math.round(Number(row.energy) * 1000 / Number(row.panelQty)) : null;
    }, [productList]);
    const [selectedCategory, setSelectedCategory] = useState("RESIDENTIAL");
    const [panelQty, setPanelQty] = useState(0);
    const [energy, setEnergy] = useState(0);
    const [residentialMatch, setResidentialMatch] = useState(null);
    // Pricing snapshot this sale was created on (null = live). Loaded from the stored
    // formPayload.pricingHistoryId so re-pricing on edit uses the SAME list — otherwise
    // a sale created on the 640W list would re-freeze to the live 650W price.
    const [pricingSnapshotId, setPricingSnapshotId] = useState(null);
    const [pricingSnapshots, setPricingSnapshots] = useState([]);
    useEffect(() => { getPricingHistories().then(r => setPricingSnapshots(r.data || [])).catch(() => {}); }, []);
    const [canEditPrice, setCanEditPrice] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("");
    const [cableBrand, setCableBrand] = useState('Any');   // wiring cable brand (user 2026-07-24)
    // RSC-style sizing verdict from the form — gates save when the config is changed.
    const [sizing, setSizing] = useState(null);
    // Item 5 — payment-method change re-populates the CC figures from the cash anchor
    // (drop any manual CC override so the display auto-derives again, as requested).
    // The load seeds ccOverride from the SAVED CC figures (so the card shows exactly what was
    // saved, not a re-derived round-trip drift — #1523: 35,177 → 35,180) and records the loaded
    // method in `_loadedPmRef`. Clear ONLY when the USER switches to a DIFFERENT method than the
    // one loaded; the mount tick (empty method) and the load itself are skipped. (user 2026-07-22)
    const _loadedPmRef = useRef(null);
    useEffect(() => {
        if (_loadedPmRef.current == null || paymentMethod === _loadedPmRef.current) return;
        setCcOverride(null);
    }, [paymentMethod]);
    // A CASH-side price edit releases the SEEDED override (fix 2026-08-31). The seed above pins
    // the CC card to the SAVED Original/Astern/Agent/Final, so changing a cash discount (Agent /
    // Upline / Team / OCP) moved the cash rows while the CC Final + Total Quotation — and the
    // FinalPrice actually PERSISTED by the save — stayed on the old number. The seed only exists
    // to stop the divide-by-factor round-trip drift on an UNTOUCHED sale (#1523), so drop it as
    // soon as the cash basis genuinely changes and let the card re-derive, exactly as the
    // payment-method change above does. A real USER edit of a CC row clears _ccSeedPristine via
    // the setCcOverride wrapper, so a manual override still wins.
    const _ccSeedPristine = useRef(false);   // ccOverride is still the untouched load seed
    const _ccSeedCashRef = useRef(null);     // cash inputs as they stood when the seed landed
    useEffect(() => {
        if (!_ccSeedPristine.current) return;
        const now = JSON.stringify([Number(ocp) || 0, Number(originalPrice) || 0,
            Number(asternDiscount) || 0, Number(agentDiscount) || 0,
            Number(uplineDiscount) || 0, Number(teamDiscount) || 0]);
        if (_ccSeedCashRef.current == null) { _ccSeedCashRef.current = now; return; }  // load settled
        if (now === _ccSeedCashRef.current) return;
        if (Number(ocp) > 0 || Number(cashRef) > 0 || Number(originalPrice) > 0) {
            // A cash anchor exists → drop the seed entirely and let the card re-derive every
            // row from cash, exactly as the payment-method change above does.
            setCcOverride(null);
        } else {
            // NO cash anchor (ocp / ATAP cashRef / Section-1 all blank — a non-standard config
            // the price list cannot match). Dropping the whole seed would re-derive Original and
            // Astern to 0, so keep those two pinned: they are the commission base. But RELEASE
            // Agent and Final so the discount the user just typed actually lands — Agent, Upline
            // and Team all divide-by-factor off the live cash inputs (they need no anchor), and
            // Final then derives as Original − Astern − Agent − Upline − Team. Without this the
            // edit was silently swallowed: the cash row moved, the CC price never did.
            setCcOverride(prev => prev ? {
                original: prev.original,
                astern: prev.astern,
                ...(prev.addon !== undefined ? { addon: prev.addon } : {}),
            } : prev);
        }
        _ccSeedPristine.current = false;
    }, [ocp, cashRef, originalPrice, asternDiscount, agentDiscount, uplineDiscount, teamDiscount]);
    const [phaseMode, setPhaseMode] = useState("single");
    const [depositOn, setDepositOn] = useState("");
    const [remarks, setRemarks] = useState("");

    const [referralOptions, setReferralOptions] = useState([]);
    const [selectedReferralId, setSelectedReferralId] = useState('');

    const userModified = useRef(false);
    // True when the agent edits any Form-Details (snapshot) field — IC/TNB/etc. appear
    // on every PDF, so such a change regenerates ALL forms on save.
    const formDetailsTouched = useRef(false);

    // saleTouched — has the user actually edited this sale (vs just opened it to view)? Drives
    // the CC add-on conversion: viewing an untouched sale shows its stored (flat) total; editing
    // any field converts it. Built from userModified (sizing/config edits) + explicit signals that
    // are all blank/equal on load (cash Section 1, OCP, CC override, line-item changes) so a plain
    // view never trips it. (user 2026-07-10 — "once edit only convert")
    const _liKey = (arr) => JSON.stringify((arr || []).map(li =>
        [String(li.itemName || li.ItemName || '').trim(), Math.round(Number(li.qty || li.Qty) || 0), Math.round((Number(li.lineTotal ?? li.LineTotal) || 0) * 100)]));
    const saleTouched = userModified.current
        || Number(originalPrice) > 0
        || Number(ocp) > 0
        // NOTE: do NOT test `!!ccOverride` here — the load now SEEDS ccOverride from the saved
        // CC figures (so the card shows what was saved), and that seeding must not count as an
        // edit. A genuine USER edit of the CC card sets userModified.current via the setter
        // wrapper, so it is still caught above. (user 2026-07-22)
        || (!!sale && _liKey(lineItems) !== _liKey(sale.lineItems));

    // The live pricing card only yields a TRUSTWORTHY customer total when it is actually
    // ANCHORED to a real basis: an editor CC/MIX override, a MIX save, a live cash anchor
    // (ocp / ATAP cashRef > 0), or a Section-1 cash edit. When NONE of those hold — e.g. the
    // ATAP cash reference resolves to 0 for a non-standard config (battery-heavy, unmatched
    // panel-qty) or a non-privileged viewer — the live card falls back to showing the ADD-ON
    // ONLY (Original 0 → Final 0 → Total = add-on). Trusting that live total would clobber the
    // sale's real quotation total on save AND in the payment-progress display (e.g. 44,900 →
    // 14,800). In that case keep the sale's SAVED total. (user 2026-08-05)
    const _liveCashAnchor = pricingLens ? (Number(ocp) > 0 ? Number(ocp) : (Number(cashRef) || 0)) : 0;
    const _s1EditedLive = originalPrice !== '' && originalPrice != null && Number(originalPrice) > 0;
    const _quoteLiveDerived = (!!ccOverride && (termForMethod(paymentMethod) > 0 || isMixMethod(paymentMethod)))
        || isMixMethod(paymentMethod)
        || (pricingLens && _liveCashAnchor > 0)
        || _s1EditedLive
        // Deduct-SST changes the SST line + customer total → always trust the live
        // (deduct-aware) quotation total as the payment denominator. (user 2026-08-10)
        || deductSst;
    const effectiveQuotationTotal = (_quoteLiveDerived && liveQuotationTotal > 0)
        ? liveQuotationTotal
        : (Number(sale?.totalPrice) || ((Number(finalPrice) || 0) + (Number(addOnPrice) || 0)));

    const fetchSale = useCallback(async () => {
        try {
            const res = await getSalesById(salesId);
            const s = res.data;
            setSale(s);

            // Keep the stored (Section-2) values for the read-only saved panel.
            setStoredPricing({
                originalPrice: s.originalPrice, finalPrice: s.finalPrice, addOnPrice: s.addOnPrice,
                asternDiscount: s.discountByAstern, agentDiscount: s.discountByAgent,
                uplineDiscount: s.discountByUpline, teamDiscount: s.discountByTeam,
            });
            // Parse the stored form snapshot up front so we can repopulate Section 1.
            let parsed = null;
            if (s.formPayloadJson) {
                try { parsed = JSON.parse(s.formPayloadJson); } catch { parsed = null; }
            }
            // Restore the "Deduct SST" flag (commercial). (user 2026-08-10)
            setDeductSst(!!(parsed && parsed.deductSst));
            // Section 1 (cash): NEW sales persist the cash inputs in section1Cash —
            // repopulate them so the cash the agent typed REMAINS visible after save.
            // OLD sales (no section1Cash) stay BLANK so they aren't re-priced unless
            // the agent enters a cash price.
            const s1 = parsed?.section1Cash;
            // The stored Astern/Agent/Upline/Team columns are in the PAYMENT-METHOD basis
            // (the save divides cash by the factor). Going back to cash means multiplying
            // by it again. This used to live inside the pricingLens branch, so only an
            // Accountant / Super Admin / Group Admin got the conversion — a view-only
            // Astern account fell through to the raw column and the CC card then divided
            // an already-CC number by the factor a second time (689 → 745 on sale 2004).
            // Hoisted so EVERY branch below reconstructs cash identically.
            // (user 2026-10-22: "why they see diff result? or the calculation is wrong")
            const _loadPmBasis = normalizePaymentMethod(s.paymentMethod);
            const _fBasis = factorForMethod(_loadPmBasis);   // 1 = cash/mix/loan, 0.925 = 36M
            // A CC sale stores its figures on the METHOD basis, so x factor recovers cash and
            // the result is rounded to a whole ringgit - the CC card's own round-RM discipline.
            // On a CASH sale the factor is 1, so that rounding converts nothing and only
            // destroys the sen the accountant typed: a 499.21 agent discount came back as 499,
            // and saving from this page then wrote 499 over the real figure. Round only when a
            // conversion actually happened. (sale 1472, user 2026-11-05)
            const _toCash = (v) => {
                const n = Number(v) || 0;
                return _fBasis === 1 ? n : Math.round(n * _fBasis);
            };
            if (pricingLens) {
                // Cash-only model (2026-06-28): the cash price comes from the ATAP dated-
                // list reference (cashRef, pushed up by GenericSalesForm) — so leave
                // Original Price + OCP BLANK and let the form anchor on cashRef. The editor
                // can override the cash in the single box. The CASH Astern discount is
                // reconstructed from the PROMO TOGGLES (deterministic, unpolluted) rather
                // than the stored discount (whose basis is ambiguous / could be polluted by
                // an earlier buggy save). Agent discount is a manual cash value → prefer the
                // snapshot, else the stored column.
                // Derive the CURRENT cash from the SALE ROW (stored method-basis × factor —
                // exact for cash/mix, recovers cash for CC). Prefer the frozen snapshot
                // (section1Cash / RSC-CSC oriCashPrice) ONLY when it AGREES with the row
                // (≤RM10) — otherwise the sale was EDITED after the snapshot was written (e.g.
                // RSC/CSC snapshots that the save didn't re-freeze), so the row is newer and
                // the snapshot would wrongly REVERT the edit page to the old price. (fix
                // 2026-06-29: sale showed stale snapshot 23,100 vs edited row 28,700.)
                const _storedCash = _toCash(s.originalPrice);
                const _storedAst = _toCash(s.discountByAstern);
                const _storedAgt = _toCash(s.discountByAgent);
                // Upline / Team ride the SAME method-basis → cash reconstruction as Agent.
                const _storedUpl = _toCash(s.discountByUpline);
                const _storedTem = _toCash(s.discountByTeam);
                const _snapCash = (s1 && Number(s1.originalPrice) > 0) ? Number(s1.originalPrice)
                    : (parsed && Number(parsed.oriCashPrice) > 0) ? Number(parsed.oriCashPrice) : 0;
                const _snapAst = (s1?.asternDiscount != null) ? Number(s1.asternDiscount)
                    : (parsed?.asternDiscount != null) ? Number(parsed.asternDiscount) : null;
                const _snapAgt = (s1?.agentDiscount != null) ? Number(s1.agentDiscount)
                    : (parsed?.agentDiscount != null) ? Number(parsed.agentDiscount) : null;
                const _snapUpl = (s1?.uplineDiscount != null) ? Number(s1.uplineDiscount)
                    : (parsed?.uplineDiscount != null) ? Number(parsed.uplineDiscount) : null;
                const _snapTem = (s1?.teamDiscount != null) ? Number(s1.teamDiscount)
                    : (parsed?.teamDiscount != null) ? Number(parsed.teamDiscount) : null;
                const _snapOk = _snapCash > 0 && _storedCash > 0 && Math.abs(_snapCash - _storedCash) <= 10;
                const _cashOri = _snapOk ? _snapCash : (_storedCash > 0 ? _storedCash : _snapCash);
                const _cashAst = (_snapOk && _snapAst != null) ? _snapAst : _storedAst;
                const _cashAgt = (_snapOk && _snapAgt != null) ? _snapAgt : _storedAgt;
                const _cashUpl = (_snapOk && _snapUpl != null) ? _snapUpl : _storedUpl;
                const _cashTem = (_snapOk && _snapTem != null) ? _snapTem : _storedTem;
                setOriginalPrice('');
                setAsternDiscount(_cashAst);
                setAgentDiscount(_cashAgt);
                setUplineDiscount(_cashUpl);
                setTeamDiscount(_cashTem);
                setOcp(_cashOri);
            } else if (s1 && Number(s1.originalPrice) > 0) {
                // Manual create-form sale — exact cash inputs the agent typed.
                setOriginalPrice(s1.originalPrice);
                setAsternDiscount(s1.asternDiscount ?? 0);
                setAgentDiscount(s1.agentDiscount ?? 0);
                // The snapshot figures are already CASH. The stored-column FALLBACK is
                // not — it is method-basis — so it needs the same × factor branch 1 applies.
                setUplineDiscount(s1.uplineDiscount ?? _toCash(s.discountByUpline));
                setTeamDiscount(s1.teamDiscount ?? _toCash(s.discountByTeam));
            } else if (parsed && Number(parsed.oriCashPrice) > 0) {
                // RSC/CSC sale — populate Section 1 (cash) from the calculator snapshot
                // so Original Price / Astern / Agent show what RSC suggested (not blank).
                setOriginalPrice(parsed.oriCashPrice);
                setAsternDiscount(parsed.asternDiscount ?? 0);
                setAgentDiscount(parsed.agentDiscount ?? 0);
                // RSC/CSC snapshots never carried the new buckets — fall back to the stored
                // column, converted BACK to cash. Without the × factor the CC card divides an
                // already-CC figure again; this is the branch sale 2004 takes, and why the
                // Astern account saw a 745 team discount against a 689 one.
                setUplineDiscount(parsed.uplineDiscount ?? _toCash(s.discountByUpline));
                setTeamDiscount(parsed.teamDiscount ?? _toCash(s.discountByTeam));
            } else {
                setOriginalPrice('');
                setAsternDiscount('');
                setAgentDiscount('');
                // Same conversion as every branch above: the column is method-basis.
                setUplineDiscount(_toCash(s.discountByUpline) || '');
                setTeamDiscount(_toCash(s.discountByTeam) || '');
            }
            setFinalPrice('');
            setAddOnPrice(s.addOnPrice);
            setCustomerName(s.customerDetails?.customerName ?? "");
            setCustomerEmail(s.customerDetails?.email ?? "");
            setCustomerPhone(s.customerDetails?.phoneNumber ?? "");
            setCustomerAddress(s.customerDetails?.address ?? "");
            setCustomerState(s.customerDetails?.state ?? "");
            setCustomerCity(s.customerDetails?.city ?? "");
            setCustomerPostcode(s.customerDetails?.postcode ?? "");
            setSelectedCategory(s.productCategory);
            setPanelQty(s.panelQty || 0);
            setEnergy(s.energy || 0);
            setPaymentMethod(normalizePaymentMethod(s.paymentMethod));
            // Restore the SAVED CC-basis figures as the override so the CC card shows EXACTLY
            // what was stored (matches the all-sales table), instead of re-deriving from cash —
            // which drifts on the ÷factor/roundUp10 round-trip (#1523: saved 35,177 → re-derived
            // 35,180). A later USER payment-method change clears it (re-derives). Direct setter
            // (not the userModified wrapper) so this restore is NOT counted as an edit.
            // (user 2026-07-22)
            {
                const _loadPm = normalizePaymentMethod(s.paymentMethod);
                _loadedPmRef.current = _loadPm;   // so the clear-effect skips the load itself
                const _loadTerm = termForMethod(_loadPm);
                const _loadIsMix = isMixMethod(_loadPm);
                // Seed the override from the SAVED figures for CC AND MIX, so the card shows
                // exactly what was stored (editable to adjust) instead of re-deriving. (2026-07-22)
                setCcOverride((_loadTerm > 0 || _loadIsMix) ? {
                    original: Number(s.originalPrice) || 0,
                    astern: Number(s.discountByAstern) || 0,
                    agent: Number(s.discountByAgent) || 0,
                    // Upline/Team were missing from the seed, so while Original/Astern/Agent/
                    // Final were pinned to the SAVED figures these two alone re-derived from
                    // cash — which is how the card showed a Final of 30,232 above rows that
                    // did not add up to it. Seeded like the rest; they are already stored in
                    // the method basis, which is exactly what this card displays.
                    ...(Number(s.discountByUpline) ? { upline: Number(s.discountByUpline) } : {}),
                    ...(Number(s.discountByTeam) ? { team: Number(s.discountByTeam) } : {}),
                    final: Number(s.finalPrice) || 0,
                    // Restore a MANUALLY-adjusted add-on ONLY (persisted separately as
                    // ccAddonOverride) so it survives reopen. When absent, leave `addon` UNSET so the
                    // card shows the ÷factor FINANCED default — NOT the flat stored value. (The
                    // earlier `addon: s.addOnPrice` pinned it to the flat number and blocked the
                    // financed default. user 2026-07-28)
                    ...(parsed?.ccAddonOverride != null && parsed.ccAddonOverride !== ''
                        ? { addon: Number(parsed.ccAddonOverride) || 0 } : {}),
                    // Untouched load seed — the card keeps the SINGLE whole-total round-up for
                    // these. Cleared the moment anyone types into a CC row. (2026-08-31)
                    _seed: true,
                } : null);
                // Arm the cash-edit release above: this override is a load SEED, not a user edit.
                _ccSeedPristine.current = (_loadTerm > 0 || _loadIsMix);
                _ccSeedCashRef.current = null;
                // MIX: restore the CASH-basis inputs from the snapshot so the card re-derives
                // correctly — the stored Sales columns are MIX-basis (factor=1) and would mis-feed
                // mixBreakdown (Astern 3,270 cash vs 3,570 MIX etc.). (user 2026-07-22/23)
                if (_loadIsMix) {
                    if (Number(parsed?.mixCashOriginal) > 0) setOcp(Number(parsed.mixCashOriginal));
                    if (parsed?.mixCashAstern != null) setAsternDiscount(Number(parsed.mixCashAstern) || 0);
                    if (parsed?.mixCashAgent != null) setAgentDiscount(Number(parsed.mixCashAgent) || 0);
                    if (parsed?.mixCashUpline != null) setUplineDiscount(Number(parsed.mixCashUpline) || 0);
                    if (parsed?.mixCashTeam != null) setTeamDiscount(Number(parsed.mixCashTeam) || 0);
                    if (parsed?.mixCashAddon != null && !(s.lineItems?.length)) setAddOnPrice(Number(parsed.mixCashAddon) || 0);
                }
            }
            // Normalize the stored phase — RSC sales store "Three Phase", create/edit store "3phase".
            // Everything downstream checks phaseMode === '3phase', so map any 3-phase format to it,
            // else the whole page (pricing + quotation inverter model) treats it as single. (user 2026-07-13)
            setPhaseMode(/3|three/i.test(String(s.phase || "")) ? "3phase" : "single");
            setDepositOn(s.depositOn ?? "");
            setRemarks("");
            setSelectedReferralId(s.referralUserId ?? '');
            setResidentialMatch(s.productCategory === "RESIDENTIAL" ? true : null);
            setLineItems(s.lineItems || []);
            // MIX (cash + CC) restore. New sales carry the cash amount in the snapshot. An OLD
            // mix sale (created before MIX existed, stored as plain cash) has none → default the
            // cash amount to the FULL total so the card stays ALL-CASH (no ÷factor), matching what
            // was saved, instead of collapsing to cashAmount=0 = full CC (#1478). (user 2026-07-23)
            const _isMixLoad = isMixMethod(normalizePaymentMethod(s.paymentMethod));
            setMixCashAmount(
                parsed?.mixCashAmount != null ? String(parsed.mixCashAmount)
                    : (_isMixLoad ? String((Number(s.finalPrice) || 0) + (Number(s.addOnPrice) || 0)) : '')
            );
            setMixTerm(Number(parsed?.mixTerm) > 0 ? Number(parsed.mixTerm) : 36);
            // Absent on every sale created before the bank picker existed -> stays blank,
            // and the flat factor keeps that sale priced exactly as it was saved.
            setCcBank(parsed?.ccBank || '');
            setCcBankMethod(parsed?.ccBankMethod || '');
            setFormPayload(parsed);
            // Restore the pricing snapshot this sale was created on (null = live) so the
            // price reference + any re-freeze use the same list it was quoted on.
            setPricingSnapshotId(parsed?.pricingHistoryId ?? null);
            setCableBrand(parsed?.cableBrand || 'Any');
            // Restore the saved promo ticks so the edit page shows what was applied at
            // the sale. On edit they are LOCKED (read-only, see genericSalesForm) so
            // they're informational only and never re-arm the Astern auto-fill — the
            // saved discount stays the source of truth (price-freeze rule). The agent
            // edits the Astern Discount field directly to change it.
            setPromoBackupbox(!!parsed?.promoBackupbox);
            setPromoFirst640(!!parsed?.promoFirst640);
            setPromoPerBatt(!!parsed?.promoPerBatt);
            setSaleStatus(s.status || 'Pending');
            statusTouched.current = false;
            userModified.current = false;
        } catch (err) {
            console.error("Failed to load sale:", err);
        } finally {
            setLoading(false);
        }
    }, [salesId]);

    useEffect(() => { fetchSale(); }, [fetchSale]);

    // Co-broke editing: load the group list + seed the current co-broke selection
    // once (when the Super Admin has the AdjustmentEdit right and the sale is in).
    useEffect(() => {
        if (!canEditCobroke || !sale) return;
        let alive = true;
        (async () => {
            try {
                const groupsRes = await getAllAgentGroups();
                if (alive) setCoBrokeGroupOptions(groupsRes.data || []);
            } catch { /* leave options empty */ }
            const curGroupId = sale.coAgentDetails?.groupId ?? "";
            const curAgentId = sale.coAgentDetails?.userId ?? "";
            if (alive) {
                setSelectedCoBrokeGroupId(curGroupId);
                setSelectedCoBrokeAgentId(curAgentId);
            }
            if (curGroupId) {
                try {
                    const agentsRes = await getAgentsByGroupId(curGroupId);
                    // Exclude the main agent so co-broke can't equal the seller.
                    const filtered = (agentsRes.data || []).filter(a => a.userId !== sale.agentDetails?.userId);
                    if (alive) setCoBrokeNameOptions(filtered);
                } catch { /* leave names empty */ }
            }
        })();
        return () => { alive = false; };
    }, [canEditCobroke, sale]);

    // Group picker change → load that group's agents + reset the selected agent.
    const handleCoBrokeGroupChange = useCallback(async (e) => {
        const gid = e?.target?.value ?? "";
        setSelectedCoBrokeGroupId(gid);
        setSelectedCoBrokeAgentId("");
        userModified.current = true;
        if (!gid) { setCoBrokeNameOptions([]); return; }
        try {
            const res = await getAgentsByGroupId(gid);
            setCoBrokeNameOptions((res.data || []).filter(a => a.userId !== sale?.agentDetails?.userId));
        } catch { setCoBrokeNameOptions([]); }
    }, [sale]);

    const loadRefund = useCallback(async () => {
        if (!salesId) return;
        try {
            const r = await getRefundBySale(salesId);
            const list = Array.isArray(r.data) ? r.data : [];
            setRefundReq(list.find(x => x.status === 'Pending') || null);
        } catch { setRefundReq(null); }
    }, [salesId]);
    useEffect(() => { loadRefund(); }, [loadRefund]);

    const submitRefundRequest = async () => {
        if (!refundReason.trim()) { alert("Please enter a refund reason."); return; }
        setRefundBusy(true);
        try {
            await createRefundRequest({ salesId: sale.salesId, reason: refundReason.trim() });
            await createLog({
                module: "Sales", recordId: sale.salesId, action: "Refund Requested",
                remarks: `Refund requested. Reason: ${refundReason.trim()}`, createdBy: user.username ?? ""
            });
            setRefundDlgOpen(false);
            setRefundReason("");
            setLogRefreshKey(k => k + 1);
            await loadRefund();
        } catch (err) {
            alert("Could not request refund: " + (err?.response?.data || err?.message || "unknown error"));
        } finally { setRefundBusy(false); }
    };

    const cancelRefund = async () => {
        if (!refundReq) return;
        if (!window.confirm("Cancel this refund request?")) return;
        setRefundBusy(true);
        try {
            await cancelRefundRequest(refundReq.refundRequestId);
            await createLog({
                module: "Sales", recordId: sale.salesId, action: "Refund Cancelled",
                remarks: "Refund request cancelled.", createdBy: user.username ?? ""
            });
            setLogRefreshKey(k => k + 1);
            await loadRefund();
        } catch (err) {
            alert("Could not cancel refund: " + (err?.response?.data || err?.message || "unknown error"));
        } finally { setRefundBusy(false); }
    };

    useEffect(() => {
        getAllAgents().then(r => {
            const refs = (r.data || []).filter(u =>
                u.status === 'Active' && (u.userRoles || []).some(ur => ur.roleName === 'Referral')
            );
            setReferralOptions(refs);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (canEditOriPrice) {
            // Snapshot-aware: load the same historical list the sale was created on so
            // re-pricing on edit matches the original quote (null = live).
            getPricing(pricingSnapshotId).then(r => setProductList((r.data || []).filter(p => p.listName === 'Residential Pricing'))).catch(() => {});
        }
    }, [canEditOriPrice, pricingSnapshotId]);

    useEffect(() => {
        const isCommercial = selectedCategory === "COMMERCIAL";
        setCanEditPrice(!isRemarkOnly && (isCommercial || canEditOriPrice));
    }, [selectedCategory, canEditOriPrice]);

    useEffect(() => {
        if (selectedCategory === "RESIDENTIAL" && energy > 0 && canEditOriPrice && userModified.current) {
            const match = productList.find(p => Number(p.energy) === Number(energy));
            setResidentialMatch(match);
            if (match) {
                // Section 1 = CASH base — auto-fill the cash column when the agent
                // changes the panel count (gated on userModified → never on load).
                const cash = phaseMode === '3phase'
                    ? (Number(match.cash3p8k) || Number(match.cashPrice) || 0)
                    : (Number(match.cashPrice) || Number(match.cash3p8k) || 0);
                setOriginalPrice(cash);
            }
        }
    }, [selectedCategory, panelQty, energy, paymentMethod, productList, canEditOriPrice]);

    useEffect(() => {
        // FinalPrice = Original − Astern − Agent − Upline − Team (spec §2). The two new
        // buckets are REAL price cuts to the customer, exactly like the Agent discount.
        const afterDisc = (originalPrice || 0) - (asternDiscount || 0) - (agentDiscount || 0)
            - (uplineDiscount || 0) - (teamDiscount || 0);
        setFinalPrice(Math.max(0, afterDisc));
    }, [originalPrice, asternDiscount, agentDiscount, uplineDiscount, teamDiscount]);

    // Post a standalone remark to the sale's history (a comment), independent of the
    // full Save Changes flow — the remarks field sits far below the Save button.
    const handleSaveRemark = async () => {
        if (!remarks.trim()) { alert("Please enter a remark."); return; }
        try {
            await createLog({
                module: "Sales", recordId: sale.salesId, action: "Remark",
                remarks: remarks.trim(), createdBy: user.username ?? ""
            });
            setRemarks("");
            setLogRefreshKey(k => k + 1);
        } catch (err) {
            console.error("Failed to save remark:", err);
            alert("Failed to save remark.");
        }
    };

    const handleSave = async () => {
        // Remark-only mode: just create a log entry with the remark
        if (isRemarkOnly) {
            if (!remarks.trim()) { alert("Please enter a remark."); return; }
            setSaving(true);
            try {
                await createLog({
                    module: "Sales", recordId: sale.salesId, action: "Remark",
                    remarks: remarks.trim(), createdBy: user.username ?? ""
                });
                setRemarks("");
                setLogRefreshKey(k => k + 1);
            } catch (err) {
                console.error("Failed to submit remark:", err);
                alert("Failed to submit remark.");
            } finally {
                setSaving(false);
            }
            return;
        }

        // No residential match: allow manual price entry (originalPrice validation in caller catches RM 0).

        // RSC-style sizing gate — on the EDIT page it's an OVERRIDABLE warning, not a hard
        // block: accounting/admin adjusting an existing sale (often a pricing-only edit, or a
        // legacy config that exceeds the ratio cap) can confirm to save anyway. Create-sales
        // still hard-blocks an invalid config. (user 2026-07-10 — "only editing sales")
        if (userModified.current && sizing && !sizing.valid) {
            const _msg = sizing.reason || "This configuration exceeds the inverter / SEDA / ratio sizing limit.";
            if (!window.confirm(`${_msg}\n\nOverride and save anyway?`)) {
                return;
            }
        }

        // Status guard — editing a Refunded/Archived sale's PRICE changes commission on
        // a sale that's already closed out. Confirm before proceeding.
        const _priceChanged = pricingLens && (
            Number(originalPrice) !== (Number(sale.originalPrice) || 0) ||
            Number(asternDiscount) !== (Number(sale.discountByAstern) || 0) ||
            Number(agentDiscount) !== (Number(sale.discountByAgent) || 0) ||
            Number(uplineDiscount || 0) !== (Number(sale.discountByUpline) || 0) ||
            Number(teamDiscount || 0) !== (Number(sale.discountByTeam) || 0));
        if (_priceChanged && (saleStatus === 'Refunded' || saleStatus === 'Archived')) {
            if (!window.confirm(`This sale is ${saleStatus}. Changing its price will recompute commission on a closed sale. Continue?`)) {
                return;
            }
        }

        setSaving(true);

        // Section 1 (cash) edited? If the agent entered a cash Original Price, convert
        // it to the chosen payment-method basis (Section 2) and save that. If blank
        // (untouched), keep the stored Section-2 values — no silent re-pricing.
        // Cash anchor for the edit-in-place (cash-only) model: the editor's Original Cash
        // Price override (ocp) wins, else the ATAP dated-list reference (cashRef). 0 = no
        // reliable cash → leave the sale's pricing untouched.
        const cashAnchor = pricingLens ? (Number(ocp) > 0 ? Number(ocp) : (Number(cashRef) || 0)) : 0;
        const s1Edited = originalPrice !== '' && originalPrice != null && Number(originalPrice) > 0;
        let pOriginal, pFinal, pAstern, pAgent, pAddon, pUpline, pTeam;
        // Upline / Team discounts must get the SAME payment-method conversion as the Agent
        // discount (spec Example F — a RM600 upline discount on a 36M CC sale stores 649).
        // methodBreakdown/mixBreakdown own that rule; this mirrors it for the branches where
        // the breakdown does not (yet) report the two new buckets back.
        const _uplineIn = Number(uplineDiscount) || 0;
        const _teamIn = Number(teamDiscount) || 0;
        const _toBasis = (v) => {
            const f = factorForMethod(paymentMethod);
            return (!f || f === 1) ? (Number(v) || 0) : Math.round((Number(v) || 0) / f);
        };
        // Add-on CC conversion (user 2026-07-10, deposit-date gate restored 2026-07-22): a CC sale
        // WITH line items stores the CC-converted add-on so the quotation total tallies with RSC —
        // BUT only for sales dated on/after the 2026-07-08 cutoff. An OLDER sale (e.g. a cash sale
        // created before then, now switched to a CC term) keeps the add-on FLAT so it isn't
        // double-converted (#1523: 341 → 390). Convert from the CASH line-item sum (fixed) so
        // re-saving re-derives the same number → no drift. Lump sales (no line items) stay flat.
        // Commission (Original − Astern) untouched. Mirrors genericSalesForm's display gate.
        const _saveTerm = termForMethod(paymentMethod);
        const _saveDepTs = depositOn ? Date.parse(depositOn) : NaN;
        const _saveDepConverts = isNaN(_saveDepTs) || _saveDepTs >= Date.parse('2026-07-08T00:00:00');
        const _cashAddonSum = (lineItems || []).reduce((s, li) => s + (Number(li.lineTotal) || 0), 0);
        const _convertSaveAddon = _saveTerm > 0 && (lineItems || []).length > 0 && saleTouched && _saveDepConverts;
        const _savedAddon = _convertSaveAddon ? ccFromCash(_cashAddonSum, _saveTerm) : (Number(addOnPrice) || 0);
        // SINGLE round-up (user 2026-08-31: "single round up is correct"). ccFromCash above rounds
        // the add-on up to RM10 on its own, and the system price is rounded up again — two
        // ratchets, so the stored AddOnPrice sat up to RM10 above the quotation total the card
        // shows. methodBreakdown rounds the WHOLE cash total once and makes the add-on the
        // residual; use that whenever the sale actually converts. (38,490 not 38,500.)
        const _bdSingle = (_convertSaveAddon && cashAnchor > 0 && _saveTerm > 0)
            ? methodBreakdown({ originalPrice: cashAnchor, asternDiscount, agentDiscount,
                uplineDiscount: _uplineIn, teamDiscount: _teamIn, addOnPrice: _cashAddonSum }, _saveTerm, { bank: ccBank, bankMethod: ccBankMethod || null })
            : null;
        // MIX (cash + CC) auto-derivation — the customer pays `mixCashAmount` in cash, the rest
        // finances on `mixTerm` months (÷factor). Commission = Original − Astern in MIX basis;
        // add-on is part of the split total (mixBreakdown handles it). (user 2026-07-22)
        const _isMixSave = isMixMethod(paymentMethod);
        const _mixCash = (pricingLens && cashAnchor > 0) ? cashAnchor : (Number(originalPrice) || 0);
        const _mixAddonCash = (lineItems || []).length > 0 ? _cashAddonSum : (Number(addOnPrice) || 0);
        const _mb = _isMixSave ? mixBreakdown({ originalPrice: _mixCash, asternDiscount, agentDiscount, uplineDiscount: _uplineIn, teamDiscount: _teamIn, addOnPrice: _mixAddonCash }, Number(mixTerm) || 36, Number(mixCashAmount) || 0, { bank: ccBank, bankMethod: ccBankMethod || null }) : null;
        if (ccOverride && (termForMethod(paymentMethod) > 0 || _isMixSave)) {
            // Direct override (Super Admin / Accountant), for CC AND MIX: store the typed figures
            // verbatim. Commission base = Original − Astern (backend rule); Final is free-form
            // (falls back to Original − Astern − Agent). CC add-on stays flat; MIX keeps its
            // financed add-on from mixBreakdown. (user 2026-07-22)
            pOriginal = Number(ccOverride.original) || 0;
            pAstern = Number(ccOverride.astern) || 0;
            // Agent mirrors Upline / Team: a released (unset) key re-derives from the live cash
            // discount instead of pinning the stale saved figure. (fix 2026-08-31)
            pAgent = (ccOverride.agent !== '' && ccOverride.agent != null)
                ? (Number(ccOverride.agent) || 0) : _toBasis(Number(agentDiscount) || 0);
            pUpline = (ccOverride.upline !== '' && ccOverride.upline != null) ? (Number(ccOverride.upline) || 0) : _toBasis(_uplineIn);
            pTeam = (ccOverride.team !== '' && ccOverride.team != null) ? (Number(ccOverride.team) || 0) : _toBasis(_teamIn);
            pFinal = (ccOverride.final !== '' && ccOverride.final != null)
                ? (Number(ccOverride.final) || 0)
                : Math.max(0, pOriginal - pAstern - pAgent - pUpline - pTeam);
            // A manually-typed add-on (ccOverride.addon) wins — so an adjusted financed add-on
            // actually persists. Otherwise MIX keeps mixBreakdown's financed add-on, CC keeps
            // its (date-gated) converted/flat add-on. Commission unaffected. (user 2026-07-28)
            pAddon = (ccOverride.addon !== '' && ccOverride.addon != null)
                ? (Number(ccOverride.addon) || 0)
                : (_isMixSave ? (_mb ? _mb.addon : (Number(addOnPrice) || 0))
                    : ((ccOverride._seed && _bdSingle) ? Math.max(0, _bdSingle.total - (Number(pFinal) || 0))
                                                       : _savedAddon));
        } else if (_isMixSave) {
            pOriginal = _mb.original; pAstern = _mb.astern; pAgent = _mb.agent; pFinal = _mb.final; pAddon = _mb.addon;
            pUpline = _mb.upline != null ? _mb.upline : _toBasis(_uplineIn);
            pTeam = _mb.team != null ? _mb.team : _toBasis(_teamIn);
        } else if (pricingLens && cashAnchor > 0) {
            // Cash-only model (2026-06-28): derive the stored (payment-method) basis from
            // the CASH anchor via the shared methodBreakdown — Original = roundUp10(cash÷
            // factor), Astern/Agent = round(cash÷factor), Final = Original − discounts (the
            // COMMISSION basis). Cash / Mix → factor 1 → cash passthrough. No ÷factor
            // back-derive of any stored value; identical to how a born-CC sale is stored.
            const _bd = methodBreakdown({ originalPrice: cashAnchor, asternDiscount, agentDiscount, uplineDiscount: _uplineIn, teamDiscount: _teamIn, addOnPrice }, termForMethod(paymentMethod), { bank: ccBank, bankMethod: ccBankMethod || null });
            pOriginal = _bd.original; pAstern = _bd.astern; pAgent = _bd.agent; pFinal = _bd.final;
            pUpline = _bd.upline != null ? _bd.upline : _toBasis(_uplineIn);
            pTeam = _bd.team != null ? _bd.team : _toBasis(_teamIn);
            // Add-on is a FLAT item price (revenue, not commission) — keep it exactly AS
            // ENTERED; do NOT CC-convert it. (Was silently changing e.g. 6,400 → 6,990 on
            // save because methodBreakdown's residual ÷factor's the add-on. fix 2026-06-29)
            // SINGLE round-up: _bd.addon is the RESIDUAL of methodBreakdown's whole-total round,
            // so FinalPrice + AddOnPrice === the quotation total the card shows. _savedAddon's
            // own ccFromCash round-up put them up to RM10 apart. (user 2026-08-31)
            pAddon = _convertSaveAddon ? _bd.addon : _savedAddon;
        } else if (s1Edited) {
            // Shared converter (RSC parity): system uses the CC price list; the TOTAL
            // rounds up the whole cash final ONCE; add-on = residual (rows sum to total).
            const _bd = methodBreakdown({ originalPrice, asternDiscount, agentDiscount, uplineDiscount: _uplineIn, teamDiscount: _teamIn, addOnPrice }, termForMethod(paymentMethod), { bank: ccBank, bankMethod: ccBankMethod || null });
            pOriginal = _bd.original;
            pAstern = _bd.astern;
            pAgent = _bd.agent;
            pUpline = _bd.upline != null ? _bd.upline : _toBasis(_uplineIn);
            pTeam = _bd.team != null ? _bd.team : _toBasis(_teamIn);
            // Add-on is a FLAT item price (revenue, not commission) — keep it exactly AS
            // ENTERED; do NOT CC-convert it. (Same fix as the cash-only branch above — this
            // s1Edited branch was still ÷factor'ing the add-on, e.g. 6,400 → 6,990 on save
            // for ID1381/1386. fix 2026-06-29)
            // SINGLE round-up: _bd.addon is the RESIDUAL of methodBreakdown's whole-total round,
            // so FinalPrice + AddOnPrice === the quotation total the card shows. _savedAddon's
            // own ccFromCash round-up put them up to RM10 apart. (user 2026-08-31)
            pAddon = _convertSaveAddon ? _bd.addon : _savedAddon;
            pFinal = _bd.final;
        } else {
            pOriginal = storedPricing?.originalPrice;
            pFinal = storedPricing?.finalPrice;
            pAstern = storedPricing?.asternDiscount;
            pAgent = storedPricing?.agentDiscount;
            pUpline = storedPricing?.uplineDiscount;
            pTeam = storedPricing?.teamDiscount;
            pAddon = storedPricing?.addOnPrice;
        }

        const salesData = {
            salesId: sale.salesId,
            updateSalesBySalesIdDto: {
                agentId: sale.agentDetails.userId,
                // Adjustment Edit: send the (possibly changed) co-broke when the
                // Super Admin has the right; otherwise re-send the existing value
                // unchanged so the backend gate never trips for a normal edit.
                coAgentId: canEditCobroke ? (selectedCoBrokeAgentId || null) : (sale.coAgentDetails?.userId ?? null),
                referralUserId: selectedReferralId || null,
                discountByAstern: pAstern || null,
                discountByAgent: pAgent || null,
                // Upline / Team discount — NULL when zero (the column is nullable and NULL
                // is treated as 0 by the backend), same convention as the two above.
                discountByUpline: pUpline || null,
                discountByTeam: pTeam || null,
                panelQty, energy, originalPrice: pOriginal, finalPrice: pFinal,
                addOnPrice: pAddon || null,
                // Customer-facing total (Cash/CC/MIX) = the on-screen quotation total / progress-bar
                // denominator. Persisted so the backend claim-state (60/100% milestones) uses the
                // same number. Fallback to final+add-on if the live total hasn't reported yet.
                quotationTotal: (_quoteLiveDerived && liveQuotationTotal > 0
                    ? liveQuotationTotal
                    : ((Number(pFinal) || 0) + (Number(pAddon) || 0)) || Number(sale?.totalPrice) || 0) || null,
                // Send the array (even EMPTY) when the sale has/had line items, so
                // removing the last one clears it. null = "no line-item table" (old
                // lump-sum sales) which the backend leaves untouched. Sending null when
                // the user emptied the table made the removed battery reappear on reload.
                lineItems: ((sale?.lineItems?.length > 0) || lineItems.length > 0) ? lineItems : null,
                productCategory: selectedCategory,
                paymentMethod,
                // Send an explicit status only when an authorised user changed it;
                // otherwise null lets the backend auto-recompute from the criteria.
                status: (canEditStatus && statusTouched.current) ? saleStatus : null,
                depositOn: convertDateStringToDateTime(depositOn) || null,
                // Persist edited PDF/form details + phase so regenerated forms match.
                // Customer name/phone/email/address are mirrored from the Customer
                // section above (single source of truth) into the snapshot.
                formPayloadJson: formPayload ? JSON.stringify({
                    ...formPayload,
                    // "Deduct SST" flag (commercial) — persisted so reopen + quotation reconstruct
                    // the SST-deducted total; dropped by JSON when false. (user 2026-08-10)
                    deductSst: deductSst || undefined,
                    // Wiring cable brand → quotation "Balance of System" line. (user 2026-07-24)
                    cableBrand: cableBrand && cableBrand !== 'Any' ? cableBrand : '',
                    // MIX (cash + CC) — persist the cash amount + term + TRUE cash original so the
                    // edit page restores them; omitted (undefined → dropped by JSON) for non-MIX.
                    // (user 2026-07-22)
                    ccBank: ccBank || undefined,
                    ccBankMethod: ccBank && ccBankMethod ? ccBankMethod : undefined,
                    mixCashAmount: _isMixSave ? (Number(mixCashAmount) || 0) : undefined,
                    mixTerm: _isMixSave ? (Number(mixTerm) || 36) : undefined,
                    // Store the CASH-basis inputs so an edit re-derives the MIX card correctly
                    // (the Sales columns hold MIX-basis figures at factor=1, which would mis-feed
                    // mixBreakdown). (user 2026-07-23)
                    mixCashOriginal: _isMixSave ? (Number(_mixCash) || 0) : undefined,
                    mixCashAstern: _isMixSave ? (Number(asternDiscount) || 0) : undefined,
                    mixCashAgent: _isMixSave ? (Number(agentDiscount) || 0) : undefined,
                    mixCashUpline: _isMixSave ? _uplineIn : undefined,
                    mixCashTeam: _isMixSave ? _teamIn : undefined,
                    mixCashAddon: _isMixSave ? (Number(_mixAddonCash) || 0) : undefined,
                    // A MANUALLY-typed CC/MIX add-on (the financed value the editor adjusted).
                    // Persist it SEPARATELY so reopen restores the edit without pinning the default
                    // for un-adjusted sales. undefined → dropped from JSON. (user 2026-07-28)
                    ccAddonOverride: (ccOverride && ccOverride.addon !== '' && ccOverride.addon != null)
                        ? (Number(ccOverride.addon) || 0) : undefined,
                    // Manual sales: re-freeze pricing from the CASH Section-1 inputs (so
                    // the quotation's cash/CC lines tally and section1Cash persists),
                    // but ONLY when Section 1 has a cash value. Untouched old sales (Section
                    // 1 blank) keep their existing frozen snapshot — no re-pricing. RSC/CSC
                    // snapshots (no 'manual' marker) are never re-frozen here.
                    // Re-freeze the quotation snapshot so a regenerated PDF matches the
                    // current price. buildFrozenPricing expects CASH inputs:
                    //  • normal (cash entry): pass the cash Section-1 inputs as-is.
                    //  • edit-in-place (isPrivileged): inputs are the METHOD/stored basis
                    //    (OSP), so back-derive cash = OSP × factor before freezing. This
                    //    keeps the DISPLAY quotation consistent; COMMISSION still comes
                    //    from the Sales row (stored directly, exact) — not this snapshot.
                    ...((formPayload.priceSource === 'manual')
                        ? (() => {
                            // Re-freeze the quotation snapshot from TRUE CASH so a regenerated
                            // PDF matches the current price. Edit-in-place (cash-only): freeze
                            // from the CASH anchor (ocp override or ATAP cashRef) + the CASH
                            // discounts shown — no ÷factor back-derive (which polluted #1686).
                            // Non-privileged create-form edit: the typed cash inputs. RSC/CSC
                            // snapshots (no 'manual' marker) are never re-frozen here.
                            const _is3p = phaseMode === '3phase';
                            let _frozen = {};
                            if (pricingLens && cashAnchor > 0) {
                                _frozen = buildFrozenPricing({ match: residentialMatch, is3p: _is3p, energy, panelQty,
                                    originalPrice: cashAnchor, asternDiscount, agentDiscount,
                                    uplineDiscount: _uplineIn, teamDiscount: _teamIn, addOnPrice,
                                    lineItems, promoBackupbox, promoFirst640, promoPerBatt, paymentMethod,
                                    mixCashAmount: Number(mixCashAmount) || 0, mixTerm: Number(mixTerm) || 36 });
                            } else if (!pricingLens && Number(originalPrice) > 0) {
                                _frozen = buildFrozenPricing({ match: residentialMatch, is3p: _is3p, energy, panelQty, originalPrice,
                                    asternDiscount, agentDiscount, uplineDiscount: _uplineIn, teamDiscount: _teamIn,
                                    addOnPrice, lineItems, promoBackupbox, promoFirst640, promoPerBatt, paymentMethod,
                                    mixCashAmount: Number(mixCashAmount) || 0, mixTerm: Number(mixTerm) || 36 });
                            } else {
                                return {};   // no reliable cash → keep the existing snapshot
                            }
                            // Item 5 — a CC override drives the customer-facing quotation total
                            // for the selected CC term (Final + flat add-on), so the PDF matches
                            // the stored commission basis (Original − Astern).
                            const _ovTerm = termForMethod(paymentMethod);
                            if (ccOverride && _ovTerm > 0) {
                                const _ovFinal = (ccOverride.final !== '' && ccOverride.final != null)
                                    ? (Number(ccOverride.final) || 0)
                                    : Math.max(0, (Number(ccOverride.original) || 0) - (Number(ccOverride.astern) || 0) - (Number(pAgent) || 0) - (Number(pUpline) || 0) - (Number(pTeam) || 0));
                                // Use the FINANCED add-on (pAddon) the CC card actually displays —
                                // NOT the raw cash addOnPrice. For a CC sale whose add-on converts
                                // (date-gated), pAddon = ccFromCash(add-on); the old code added the
                                // un-converted cash add-on, so the frozen quotation total (and PDF)
                                // came out ~9% low vs the card, e.g. 30,683 instead of 31,370
                                // (sale 1839). pAddon already honours ccOverride.addon + the flat
                                // fallback, so cash / old / lump sales are unchanged. (user 2026-08-07)
                                const _ovTotal = _ovFinal + (Number(pAddon) || 0);
                                const _ovDisc = Math.max(0, (Number(ccOverride.original) || 0) - _ovFinal);
                                _frozen = { ..._frozen, quoteTermPrice: _ovTotal,
                                    ...(_ovTerm === 36 ? { cc36mPrice: _ovTotal, cc36Discount: _ovDisc } : {}),
                                    ...(_ovTerm === 60 ? { cc60mPrice: _ovTotal, cc60Discount: _ovDisc } : {}) };
                            }
                            return _frozen;
                        })()
                        : {}),
                    // ALWAYS persist the latest CASH inputs so REOPENING the edit page shows
                    // what was saved — not a stale snapshot. The load reads section1Cash for the
                    // cash price; RSC/CSC snapshots (priceSource ≠ 'manual') aren't re-frozen
                    // above, so without this their cash reverted to the old frozen value on every
                    // open. (fix 2026-06-29). Manual sales already wrote the same values via the
                    // re-freeze; this just makes it unconditional for any edit-in-place save.
                    ...(pricingLens && cashAnchor > 0
                        ? { section1Cash: { originalPrice: cashAnchor, asternDiscount: Number(asternDiscount) || 0, agentDiscount: Number(agentDiscount) || 0, uplineDiscount: _uplineIn, teamDiscount: _teamIn, addOnPrice: Number(addOnPrice) || 0 } }
                        : {}),
                    name: (customerName || '').toUpperCase(),
                    phone: customerPhone || '',
                    email: (customerEmail || '').toLowerCase(),
                    address: combinedAddress,
                })
                // A sale with NO snapshot had nowhere to keep a manual CC add-on override, so
                // the typed figure vanished on save and the field appeared stuck (sale 1484:
                // 7,700 typed, 7,400 saved). Write a MINIMAL snapshot carrying only the
                // override. (user 2026-08-21: "allow edit but dont allow backfill")
                //
                // Deliberately nothing else in it — no priceSource, no section1Cash, no frozen
                // prices. That keeps showPromos false and the re-freeze branch out of reach, so
                // this CANNOT re-price the sale or resurrect old pricing. It only stores the one
                // number the editor typed, for the sale being edited. Sales that already have a
                // snapshot are untouched, and no other sale is written to at all.
                : ((ccOverride && ccOverride.addon !== '' && ccOverride.addon != null)
                    ? JSON.stringify({ ccAddonOverride: Number(ccOverride.addon) || 0 })
                    : null),
                phase: phaseMode === '3phase' ? '3phase' : 'single'
            },
            updateCustomerBySalesId: {
                customerName: customerName.toUpperCase(), email: (customerEmail || '').toLowerCase(), phoneNumber: customerPhone, address: (customerAddress || '').toUpperCase(),
                state: customerState || null, city: customerCity || null, postcode: customerPostcode || null
            },
            updatedBy: user.username,
            remarks: remarks || null
        };

        try {
            const res = await updateSalesByAdmin(salesData);
            if (res.status === 200) {
                try {
                    await createLog({
                        module: "Sales", recordId: sale.salesId, action: "Update",
                        // The update response omits `scheme`, which made the change-history
                        // show a phantom "Scheme: ATAP → —" on every save (the DB value is
                        // never touched). Carry the unchanged scheme into newValue so the
                        // diff doesn't report a false change.
                        oldValue: JSON.stringify(sale), newValue: JSON.stringify({ ...res.data, scheme: sale.scheme }),
                        remarks, createdBy: user.username ?? ""
                    });
                } catch { /* log failed silently */ }
                // Field-aware regeneration (user-confirmed 2026-06-18): regenerate only
                // the forms the changed fields affect. Customer/personal info or any
                // Form-Details edit → ALL forms; system size (panels/kWp/phase) →
                // quotation + atap (the contract states the system); price/discount/
                // battery/add-ons/payment method → quotation only. No change → no regen.
                const _n = (v) => Number(v) || 0;
                const _li = (arr) => JSON.stringify((arr || []).map(li => [String(li.itemName || '').trim(), _n(li.qty), _n(li.lineTotal)]));
                const cd = sale.customerDetails || {};
                const customerChanged =
                    (customerName || '').toUpperCase() !== (cd.customerName || '').toUpperCase() ||
                    (combinedAddress || '') !== (cd.address || '') ||
                    (customerEmail || '').toLowerCase() !== (cd.email || '').toLowerCase() ||
                    (customerPhone || '') !== (cd.phoneNumber || '') ||
                    (customerState || '') !== (cd.state || '') ||
                    (customerCity || '') !== (cd.city || '') ||
                    (customerPostcode || '') !== (cd.postcode || '');
                const detailsChanged = formDetailsTouched.current;   // IC/TNB/etc. in the snapshot
                const systemChanged =
                    _n(panelQty) !== _n(sale.panelQty) ||
                    _n(energy) !== _n(sale.energy) ||
                    String(phaseMode === '3phase' ? '3phase' : 'single') !== String(sale.phase || 'single');
                const pricingChanged =
                    _n(pOriginal) !== _n(sale.originalPrice) ||
                    _n(pAstern) !== _n(sale.discountByAstern) ||
                    _n(pAgent) !== _n(sale.discountByAgent) ||
                    _n(pUpline) !== _n(sale.discountByUpline) ||
                    _n(pTeam) !== _n(sale.discountByTeam) ||
                    _n(pAddon) !== _n(sale.addOnPrice) ||
                    _n(pFinal) !== _n(sale.finalPrice) ||
                    String(paymentMethod || '') !== normalizePaymentMethod(sale.paymentMethod) ||
                    _li(lineItems) !== _li(sale.lineItems);
                let regenTypes = undefined;     // undefined = nothing changed
                if (customerChanged || detailsChanged) regenTypes = null;           // null = ALL forms
                else if (systemChanged) regenTypes = ['quotation', 'atap'];
                else if (pricingChanged) regenTypes = ['quotation'];
                if (regenTypes !== undefined) {
                    try { await generateSaleForms(sale.salesId, false, regenTypes); } catch (e) { console.error("Auto-regenerate forms failed:", e); }
                }
                alert(regenTypes !== undefined
                    ? `Sales updated — ${regenTypes === null ? 'all forms' : regenTypes.join(' + ')} regenerated.`
                    : "Sales updated successfully.");
                formDetailsTouched.current = false;
                setRemarks("");
                setLogRefreshKey(k => k + 1);
                fetchSale();
            }
        } catch (err) {
            console.error("Failed to update sales:", err);
            const data = err.response?.data;
            const serverMsg = (data && (data.message || data.title))
                || (typeof data === 'string' ? data : '');
            alert(serverMsg || "Failed to update sales.");
        } finally {
            setSaving(false);
        }
    };

    // Edit a single field inside the parsed form snapshot (immutable update).
    const handleFormFieldChange = (key, value) => {
        setFormPayload(prev => ({ ...(prev || {}), [key]: value }));
        userModified.current = true;
        formDetailsTouched.current = true;   // snapshot/PDF detail edited → regen all forms
    };

    // Combined one-line address (same format the RSC builder uses) so the snapshot
    // / PDF address always matches the split Customer-section fields above.
    const combinedAddress = [
        customerAddress,
        [customerPostcode, customerCity].filter(s => s && String(s).trim()).join(' '),
        customerState,
    ].filter(s => s && String(s).trim()).join(', ').toUpperCase();

    // Build the ClickUp submission message from the sale + snapshot. Extracts
    // existing data only — adds nothing.
    const buildClickupMsg = () => {
        const agentName = handleAgentName(sale.agentDetails?.firstName, sale.agentDetails?.lastName, sale.agentDetails?.alias);
        return buildClickupExportMsg({ sale, formPayload, agentName, combinedAddress });
    };
    const handleClickupCopy = async () => {
        try {
            await navigator.clipboard.writeText(buildClickupMsg());
            setClickupCopied(true);
            setTimeout(() => setClickupCopied(false), 1800);
        } catch { alert('Could not copy to clipboard.'); }
    };
    const handleClickupWhatsApp = () => {
        window.open(`https://wa.me/?text=${encodeURIComponent(buildClickupMsg())}`, '_blank');
    };

    const openPaymentLinkDialog = () => {
        // Default amount = 100% of the total (Final + Add-on).
        const total = sale?.totalPrice ?? ((finalPrice || 0) + (addOnPrice || 0));
        setPlBank("");
        setPlMethod("One-off payment");
        setPlAmount(total > 0 ? String(total) : "");
        setPlNumLinks(1);
        setPlMsg(null);
        setPlOpen(true);
    };

    const handleRequestPaymentLink = async () => {
        setPlMsg(null);
        const amt = parseFloat(plAmount);
        if (!plBank) { setPlMsg({ type: 'error', text: 'Please select a bank.' }); return; }
        if (!(amt > 0)) { setPlMsg({ type: 'error', text: 'Please enter a valid amount.' }); return; }
        const links = parseInt(plNumLinks, 10);
        if (!(links >= 1)) { setPlMsg({ type: 'error', text: 'Number of links must be at least 1.' }); return; }
        setPlSubmitting(true);
        try {
            await requestPaymentLink({
                salesId: sale.salesId,
                bank: plBank,
                paymentMethod: plMethod,
                amount: amt,
                numLinks: links,
            });
            setPlMsg({ type: 'success', text: 'Payment link requested. It will appear in Remarks & History once filled.' });
            setLogRefreshKey(k => k + 1);
            setTimeout(() => setPlOpen(false), 1200);
        } catch {
            setPlMsg({ type: 'error', text: 'Could not submit the request. Please try again.' });
        } finally {
            setPlSubmitting(false);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
    if (!sale) return <Box sx={{ p: 4, textAlign: 'center' }}><Typography>Sale not found.</Typography></Box>;

    return (
        <Box sx={{ width: '100%' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
                sx={{ mb: 2, fontFamily: PP, textTransform: 'none', color: '#64748B', fontWeight: 500, fontSize: '0.85rem' }}>
                Back
            </Button>

            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', mb: 0.5 }}>
                Sale #{sale.salesId}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontFamily: PP, mb: 2 }}>
                {sale.customerDetails?.customerName} &middot; {handleAgentName(sale.agentDetails?.firstName, sale.agentDetails?.lastName, sale.agentDetails?.alias)}
            </Typography>

            {/* Two-column layout: left = form, right = sticky save + collapsible documents + remarks/history */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.5fr) minmax(380px, 1fr)' },
                gap: 2,
                alignItems: 'start',
            }}>
                {/* Left column: sales form on top, doc-only form details at the very end */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                    <GenericSalesForm
                        mode="edit"
                        saleTouched={saleTouched}
                        isSuperAdmin={isSuperAdmin}
                        canEditOriPrice={canEditOriPrice}
                        agentGroup={sale.agentDetails?.groupName}
                        agentName={handleAgentName(sale.agentDetails?.firstName, sale.agentDetails?.lastName, sale.agentDetails?.alias)}
                        coBrokeGroupName={sale.coAgentDetails?.groupName}
                        coBrokeAgentName={sale.coAgentDetails ? handleAgentName(sale.coAgentDetails.firstName, sale.coAgentDetails.lastName, sale.coAgentDetails.alias) : ""}
                        disableCoBrokeFields={!canEditCobroke}
                        handleCoBrokeGroupChange={handleCoBrokeGroupChange}
                        coBrokeGroupOptions={canEditCobroke ? coBrokeGroupOptions : []}
                        coBrokeNameOptions={canEditCobroke ? coBrokeNameOptions : []}
                        selectedCoBrokeGroupId={canEditCobroke ? selectedCoBrokeGroupId : sale.coAgentDetails?.groupId}
                        setSelectedCoBrokeGroupId={() => {}}
                        selectedCoBrokeAgentId={canEditCobroke ? selectedCoBrokeAgentId : sale.coAgentDetails?.userId}
                        setSelectedCoBrokeAgentId={canEditCobroke ? ((v) => { setSelectedCoBrokeAgentId(v); userModified.current = true; }) : () => {}}
                        referralOptions={referralOptions}
                        selectedReferralId={selectedReferralId}
                        setSelectedReferralId={(val) => { setSelectedReferralId(val); userModified.current = true; }}
                        disableReferralField={!canEditReferral}
                        customerName={customerName} setCustomerName={setCustomerName}
                        customerEmail={customerEmail} setCustomerEmail={setCustomerEmail}
                        customerPhone={customerPhone} setCustomerPhone={setCustomerPhone}
                        customerAddress={customerAddress} setCustomerAddress={setCustomerAddress}
                        customerState={customerState} setCustomerState={setCustomerState}
                        customerCity={customerCity} setCustomerCity={setCustomerCity}
                        customerPostcode={customerPostcode} setCustomerPostcode={setCustomerPostcode}
                        customerIc={sale.customerDetails?.ic || formPayload?.icNumber || ''}
                        selectedCategory={selectedCategory}
                        setSelectedCategory={(val) => { setSelectedCategory(val); userModified.current = true; }}
                        cableBrand={cableBrand}
                        setCableBrand={(val) => { setCableBrand(val); userModified.current = true; }}
                        phaseMode={phaseMode}
                        setPhaseMode={(val) => { setPhaseMode(val); userModified.current = true; }}
                        panelQty={panelQty}
                        setPanelQty={(val) => { setPanelQty(val); setEnergy(Number((val * 0.62).toFixed(2))); userModified.current = true; }}
                        energy={energy} setEnergy={setEnergy}
                        originalPrice={originalPrice} setOriginalPrice={setOriginalPrice}
                        salesPriceLens={pricingLens}
                        canEditCashPrice={isPrivileged}
                        ccOverride={ccOverride} setCcOverride={(v) => { setCcOverride(v); userModified.current = true; _ccSeedPristine.current = false; }}
                        canEditCcPricing={isPrivileged}
                        mixCashAmount={mixCashAmount} setMixCashAmount={(v) => { setMixCashAmount(v); userModified.current = true; }}
                        mixTerm={mixTerm} setMixTerm={(v) => { setMixTerm(v); userModified.current = true; }}
                        ocp={ocp} setOcp={(v) => { setOcp(v); userModified.current = true; }}
                        onCashRef={setCashRef}
                        onQuotationTotal={setLiveQuotationTotal}
                        residentialMatch={residentialMatch}
                        pricingSnapshotId={pricingSnapshotId}
                        setPricingSnapshotId={(v) => { setPricingSnapshotId(v); userModified.current = true; setFormPayload(prev => ({ ...(prev || {}), pricingHistoryId: v || null })); }}
                        pricingSnapshots={pricingSnapshots}
                        atapPanelWatt={atapPanelWatt}
                        initialPanelWattage={formPayload?.panelWatt}
                        availablePaymentMethods={(paymentMethod && !BASE_PAYMENT_METHODS.some(m => m.value === paymentMethod))
                          ? [...BASE_PAYMENT_METHODS, { label: paymentMethod, value: paymentMethod }]
                          : BASE_PAYMENT_METHODS}
                        paymentMethod={paymentMethod}
                        setPaymentMethod={(val) => { setPaymentMethod(val); userModified.current = true; }}
                        ccBank={ccBank} setCcBank={(v) => { setCcBank(v); userModified.current = true; }}
                        ccBankMethod={ccBankMethod} setCcBankMethod={setCcBankMethod}
                        canEditPrice={canEditPrice}
                        depositOn={depositOn} setDepositOn={setDepositOn}
                        asternDiscount={asternDiscount} setAsternDiscount={setAsternDiscount}
                        agentDiscount={agentDiscount} setAgentDiscount={setAgentDiscount}
                        uplineDiscount={Number(uplineDiscount) || 0}
                        setUplineDiscount={setUplineDiscount}
                        setTeamDiscount={setTeamDiscount}
                        teamDiscount={Number(teamDiscount) || 0}
                        deductSst={deductSst} setDeductSst={setDeductSst}
                        finalPrice={finalPrice} setFinalPrice={setFinalPrice}
                        addOnPrice={addOnPrice} setAddOnPrice={setAddOnPrice}
                        disableAgentFields={true}
                        // All inputs editable by the agent only while Pending; always by
                        // Accountant / Super Admin. (canEditDetails / canEditPricing are
                        // status-aware.)
                        disableCustomerFields={!canEditDetails}
                        disablePricingFields={!canEditPricing}
                        disableProductFields={!canEditDetails}
                        disableDepositDate={!canEditDetails}
                        handleAgentGroupChange={() => {}}
                        lineItems={lineItems}
                        setLineItems={setLineItems}
                        canEditAddons={canEditPricing}
                        showPromos={formPayload?.priceSource === 'manual'}
                        promoBackupbox={promoBackupbox} setPromoBackupbox={setPromoBackupbox}
                        promoFirst640={promoFirst640} setPromoFirst640={setPromoFirst640}
                        promoPerBatt={promoPerBatt} setPromoPerBatt={setPromoPerBatt}
                        // Unlock promos while the sale is Pending (still adjustable); lock
                        // once Active/signed so the frozen discount isn't auto-clobbered.
                        promosLocked={(sale?.status || 'Pending') !== 'Pending'}
                        savedPricing={storedPricing} savedPaymentMethod={paymentMethod}
                        onSizingChange={setSizing}
                    />
                </Paper>

                {/* Document / form details (not sales-related): emergency contact, TIN,
                    IC, TNB account, etc. — kept at the very end of the left column.
                    Feeds the generated PDFs; editing here re-syncs the form snapshot. */}
                <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                    <Box
                        onClick={() => setFormDetailsOpen(o => !o)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    >
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.95rem' }}>
                                Form Details (for PDFs)
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: PP }}>
                                Customer / document info used only by the generated forms — not part of the sale.
                            </Typography>
                        </Box>
                        <IconButton size="small" sx={{ color: '#64748B' }}>
                            {formDetailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Collapse in={formDetailsOpen} unmountOnExit>
                        <Box sx={{ mt: 2 }}>
                            <SaleFormDetails
                                payload={formPayload}
                                onChange={handleFormFieldChange}
                                canEdit={canEditDetails}
                                lockedFields={{
                                    name: (customerName || '').toUpperCase(),
                                    phone: customerPhone || '',
                                    email: (customerEmail || '').toLowerCase(),
                                    address: combinedAddress,
                                }}
                            />
                        </Box>
                    </Collapse>
                </Paper>
                </Box>

                {/* Right column: sticky Save button + collapsible Documents + Remarks/History.
                    Scrolls independently when taller than the viewport so you don't have to
                    scroll the left column down first. */}
                <Box sx={{ minWidth: 0, position: 'sticky', top: 16, alignSelf: 'start', maxHeight: { md: 'calc(100vh - 32px)' }, overflowY: { md: 'auto' } }}>
                    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        {/* Sale-status (left) + Save (right), equal width */}
                        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, mb: 1 }}>
                            {(() => {
                                const st = (canEditStatus ? saleStatus : sale.status) || 'Pending';
                                const s = STATUS_STYLES[st] || STATUS_STYLES.Pending;
                                if (canEditStatus) {
                                    return (
                                        <Select
                                            size="small"
                                            value={saleStatus}
                                            onChange={(e) => { setSaleStatus(e.target.value); statusTouched.current = true; }}
                                            sx={{
                                                flex: 1,
                                                fontFamily: PP, fontWeight: 700, fontSize: '0.82rem', color: s.fg,
                                                background: s.bg, borderRadius: '8px',
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: s.bd },
                                                '& .MuiSelect-select': { py: 0.9 },
                                            }}
                                        >
                                            {['Pending', 'Active', 'Dispute Pricing', 'Refunded', 'Archived'].map(o => (
                                                <MenuItem key={o} value={o} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>{o}</MenuItem>
                                            ))}
                                        </Select>
                                    );
                                }
                                return (
                                    <Box sx={{
                                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: '8px', background: s.bg, border: `1px solid ${s.bd}`,
                                    }}>
                                        <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.82rem', color: s.fg, whiteSpace: 'nowrap' }}>
                                            {st}
                                        </Typography>
                                    </Box>
                                );
                            })()}
                            <Button
                                onClick={handleSave}
                                variant="contained"
                                disabled={saving}
                                sx={{
                                    flex: 1,
                                    background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                                    fontFamily: PP, fontWeight: 600, textTransform: 'none',
                                    borderRadius: '8px', boxShadow: 'none', fontSize: '0.85rem',
                                }}
                            >
                                {saving ? 'Saving...' : isRemarkOnly ? 'Submit Remark' : 'Save Changes'}
                            </Button>
                        </Box>

                        {/* Activation checklist — what's needed before the sale goes Active.
                            Shown to all roles (read-only info), including Group Admin. */}
                        {sale.status !== 'Refunded' && sale.status !== 'Archived' && (() => {
                            const crit = [
                                { ok: !!sale.hasDepositDoc, label: 'Deposit document uploaded' },
                                { ok: !!sale.isQuotationSigned, label: 'Quotation signed' },
                                { ok: !!depositOn, label: 'Deposit date set' },
                            ];
                            const allOk = crit.every(c => c.ok);
                            return (
                                <Box sx={{ mb: 2.5, p: 1.25, borderRadius: '8px', bgcolor: allOk ? '#F0FDF4' : '#FFFBEB', border: `1px solid ${allOk ? '#BBF7D0' : '#FDE68A'}` }}>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', fontWeight: 600, color: allOk ? '#15803D' : '#B45309', mb: 0.5 }}>
                                        {allOk ? 'All criteria met — sale is Active.' : 'Pending — complete these to activate:'}
                                    </Typography>
                                    {crit.map(c => (
                                        <Box key={c.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                            {c.ok
                                                ? <CheckCircleIcon sx={{ fontSize: 15, color: '#22C55E' }} />
                                                : <RadioButtonUncheckedIcon sx={{ fontSize: 15, color: '#CBD5E1' }} />}
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: c.ok ? '#475569' : '#92400E' }}>
                                                {c.label}
                                            </Typography>
                                        </Box>
                                    ))}
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.66rem', color: '#94A3B8', mt: 0.5 }}>
                                        Status updates automatically once all three are done. Re-open the sale after uploading the deposit slip or after the customer signs.
                                    </Typography>
                                </Box>
                            );
                        })()}
                    </Paper>

                    {/* Box B — Documents: signature · ClickUp · regenerate · payment link · docs */}
                    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, mt: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        <SalesDocumentsPanel
                            salesId={sale.salesId}
                            canEdit={canUploadDocs}
                            signedOn={sale.signedOn}
                            depositOn={sale.depositOn}
                            isCommercial={sale.isCommercial}
                            customerSigned={sale.customerSigned}
                            agentSigned={sale.agentSigned}
                            canAgentSign={(() => {
                                const me = Number(localStorage.getItem("UserId")) || 0;
                                return me > 0 && (Number(sale?.agentDetails?.userId) === me
                                    || Number(sale?.coAgentDetails?.userId) === me);
                            })()}
                            refreshKey={logRefreshKey}
                            onDocsChanged={fetchSale}
                            paidTotal={sale.paidTotal || 0}
                            totalPrice={effectiveQuotationTotal}
                            extraActions={(
                                <Box>
                                    <Typography sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                                        ClickUp Export Msg
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleClickupCopy}
                                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', borderRadius: '8px', borderColor: '#CBD5E1', color: '#475569' }}
                                        >
                                            {clickupCopied ? 'Copied' : 'Copy'}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleClickupWhatsApp}
                                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', background: 'linear-gradient(135deg,#25D366,#128C7E)', '&:hover': { background: 'linear-gradient(135deg,#20b558,#0f7360)', boxShadow: 'none' } }}
                                        >
                                            WhatsApp
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                            afterActions={(
                                <Box>
                                    <Typography sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75 }}>
                                        Payments
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={openPaymentLinkDialog}
                                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', borderColor: '#CBD5E1', color: '#1A8CFF' }}
                                        >
                                            Request Payment Link
                                        </Button>
                                        {refundReq ? (
                                            <Button size="small" variant="outlined" disabled={refundBusy} onClick={cancelRefund}
                                                sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', borderColor: '#FCA5A5', color: '#DC2626' }}>
                                                Cancel Refund
                                            </Button>
                                        ) : (
                                            <Button size="small" variant="outlined" disabled={refundBusy} onClick={() => { setRefundReason(""); setRefundDlgOpen(true); }}
                                                sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', borderColor: '#CBD5E1', color: '#DC2626' }}>
                                                Request Refund
                                            </Button>
                                        )}
                                    </Box>
                                    {refundReq && (
                                        <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#DC2626', mt: 0.5 }}>
                                            Refund requested{refundReq.requestedByName ? ` by ${refundReq.requestedByName}` : ''}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        />
                    </Paper>

                    {/* Project Timeline — milestone dates (Accountant / Admin / Super Admin edit) */}
                    {sale && (
                        <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, mt: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                            <ProjectTimeline sale={sale} canEdit={canEditTimeline} />
                        </Paper>
                    )}

                    {/* Box C — Remarks & History */}
                    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, mt: 2, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        <Box
                            onClick={() => setHistoryOpen(o => !o)}
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                cursor: 'pointer', py: 1,
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.9rem' }}>
                                Remarks &amp; History
                            </Typography>
                            <IconButton size="small" sx={{ color: '#64748B' }}>
                                {historyOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Box>
                        <Collapse in={historyOpen} unmountOnExit>
                            <Box sx={{ pt: 0.5 }}>
                                <LogViewer module="Sales" recordId={sale?.salesId} salesId={sale?.salesId} remarks={remarks} setRemarks={setRemarks} showOnly="sales" refreshKey={logRefreshKey} onSaveRemark={handleSaveRemark} />
                            </Box>
                        </Collapse>
                    </Paper>
                </Box>
            </Box>

            {/* Request Payment Link dialog */}
            <Dialog open={plOpen} onClose={() => !plSubmitting && setPlOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}>
                    Request Payment Link
                </DialogTitle>
                <DialogContent>
                    {plMsg && (
                        <Alert severity={plMsg.type} sx={{ mb: 2, fontFamily: PP, fontSize: '0.8rem' }}>{plMsg.text}</Alert>
                    )}
                    <FormControl fullWidth size="small" margin="dense">
                        <InputLabel sx={{ fontFamily: PP }}>Payment method</InputLabel>
                        <Select
                            label="Payment method"
                            value={plMethod}
                            onChange={(e) => { setPlMethod(e.target.value); setPlBank(''); }}
                            sx={{ fontFamily: PP }}
                        >
                            {PL_METHODS.map(m => (
                                <MenuItem key={m} value={m} sx={{ fontFamily: PP }}>{m}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" margin="dense">
                        <InputLabel sx={{ fontFamily: PP }}>Bank</InputLabel>
                        <Select
                            label="Bank"
                            value={plBank}
                            onChange={(e) => setPlBank(e.target.value)}
                            sx={{ fontFamily: PP }}
                        >
                            {banksForMethod(plMethod).map(b => (
                                <MenuItem key={b} value={b} sx={{ fontFamily: PP }}>{b}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Amount (RM)"
                        type="number"
                        value={plAmount}
                        onChange={(e) => setPlAmount(e.target.value)}
                        fullWidth
                        size="small"
                        margin="dense"
                        InputProps={{ sx: { fontFamily: PP } }}
                        InputLabelProps={{ sx: { fontFamily: PP } }}
                    />
                    <TextField
                        label="Number of links"
                        type="number"
                        value={plNumLinks}
                        onChange={(e) => setPlNumLinks(e.target.value)}
                        fullWidth
                        size="small"
                        margin="dense"
                        inputProps={{ min: 1 }}
                        InputProps={{ sx: { fontFamily: PP } }}
                        InputLabelProps={{ sx: { fontFamily: PP } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPlOpen(false)} disabled={plSubmitting}
                        sx={{ fontFamily: PP, textTransform: 'none', color: '#64748B' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleRequestPaymentLink} variant="contained" disabled={plSubmitting}
                        sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}>
                        {plSubmitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Request Refund dialog */}
            <Dialog open={refundDlgOpen} onClose={() => !refundBusy && setRefundDlgOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}>
                    Request Refund
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#64748B', mb: 1.5 }}>
                        This flags the sale as refund-requested and logs it in Remarks &amp; History. You can cancel it again anytime.
                    </Typography>
                    <TextField
                        autoFocus fullWidth multiline minRows={2} size="small"
                        label="Reason" value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        sx={{ fontFamily: PP, '& .MuiInputBase-root': { fontFamily: PP, fontSize: '0.85rem' } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRefundDlgOpen(false)} disabled={refundBusy} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={submitRefundRequest} disabled={refundBusy} variant="contained"
                        sx={{ fontFamily: PP, textTransform: 'none', background: '#DC2626', '&:hover': { background: '#B91C1C' } }}>
                        {refundBusy ? 'Submitting…' : 'Request Refund'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
